import React, { useMemo, useState } from 'react';
import { Button, Icon, Input, Popconfirm, Switch, Tag, Tooltip } from 'antd';
import styled from 'styled-components';

import { department as departmentName } from '~/components/RegisterTasks/Utils';
import { frequency } from './helpers';
import { say } from './ruleUi';
import GroupRow from './GroupRow';
import { isMarkedNoRule, NO_RULE_HELP, MISSING_RULE_HELP } from './enrollment';

// ---------------------------------------------------------------------------
// Etapa 2 da aba Regra: tarefas sem regra, escolhidas à mão.
//
// Lista com busca, filtro de situação e de departamento, rolagem própria e
// grupos por departamento. A linha inteira liga/desliga, e a ação em lote vale
// para o que está filtrado — é assim que se liga "tudo do Pessoal" de uma vez.
// Grupos de tarefas (TFE...) entram como uma linha só, que abre nas filhas.
// ---------------------------------------------------------------------------

const Box = styled.div`
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
`;

const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
`;

const Pills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px 14px 0;
`;

const Pill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 10px;
  border-radius: 14px;
  border: 1px solid #d9d9d9;
  background: #fff;
  color: #595959;
  font-size: 12px;
  cursor: pointer;

  em {
    font-style: normal;
    font-size: 11px;
    color: #8c8c8c;
  }
  &[aria-pressed='true'] {
    border-color: #1890ff;
    background: #e6f7ff;
    color: #0050b3;
    font-weight: 500;
  }
  &[aria-pressed='true'] em { color: #1890ff; }
`;

const List = styled.div`
  max-height: 380px;
  overflow-y: auto;
  margin-top: 10px;
  border-top: 1px solid #f0f0f0;
`;

const Group = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  padding: 6px 14px;
  background: #f5f7fa;
  border-bottom: 1px solid #f0f0f0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #8c8c8c;
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 14px;
  border-bottom: 1px solid #f7f7f7;
  cursor: pointer;
  background: #fff;
  transition: background 0.12s;

  &[aria-checked='true'] { background: #f6fbff; }
  &[aria-checked='true'] .name { font-weight: 500; }
  &:hover { background: #f0f7ff; }

  .name {
    flex: 1;
    min-width: 0;
    font-size: 13px;
    color: #262626;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .when {
    font-size: 12px;
    color: #8c8c8c;
    white-space: nowrap;
  }
`;

const Empty = styled.div`
  padding: 28px 14px;
  text-align: center;
  font-size: 13px;
  color: #8c8c8c;
`;

const STATES = [
  { key: 'all', label: 'Todas' },
  { key: 'on', label: 'Ligadas' },
  { key: 'off', label: 'Desligadas' },
];

const deptName = (department) => departmentName(department) || 'Sem departamento';

const ManualTasks = ({ tasks, groups, onToggle, busyIds }) => {
  const [search, setSearch] = useState('');
  const [state, setState] = useState('all');
  const [dept, setDept] = useState(null);
  const busy = busyIds || [];
  const groupList = groups || [];

  // Cada linha é uma tarefa solta ou um grupo inteiro.
  const entries = useMemo(() => tasks.map((task) => ({
    key: 't' + task.id, name: task.name, department: task.department, on: !!task.is_assigned, task,
  })).concat(groupList.map((g) => ({
    key: 'g' + g.group.id, name: g.group.name, department: g.group.department, on: g.status.on > 0, group: g,
  }))), [tasks, groupList]);

  const term = search.trim().toLowerCase();
  const has = (name) => String(name || '').toLowerCase().indexOf(term) > -1;
  const matchesText = (entry) => !term || has(entry.name) || (entry.group && entry.group.items.some((task) => has(task.name)));
  const matchesState = (entry) => state === 'all' || (state === 'on' ? entry.on : !entry.on);

  const departments = useMemo(() => {
    const map = {};
    entries.forEach((entry) => {
      const key = String(entry.department || 0);
      if (!map[key]) {
        map[key] = { key, name: deptName(entry.department), count: 0 };
      }
      map[key].count++;
    });
    return Object.keys(map).map((k) => map[k]).sort((a, b) => a.name.localeCompare(b.name));
  }, [entries]);

  const filtered = entries.filter((entry) => (
    matchesText(entry) && matchesState(entry) && (!dept || String(entry.department || 0) === dept)
  ));

  const sections = [];
  filtered.forEach((entry) => {
    const name = deptName(entry.department);
    let section = sections.filter((g) => g.name === name)[0];
    if (!section) {
      section = { name, entries: [] };
      sections.push(section);
    }
    section.entries.push(entry);
  });
  sections.sort((a, b) => a.name.localeCompare(b.name));
  // Grupos primeiro, depois as tarefas soltas, cada um em ordem alfabética.
  sections.forEach((g) => g.entries.sort((a, b) => (
    (a.group ? 0 : 1) - (b.group ? 0 : 1) || String(a.name).localeCompare(String(b.name))
  )));

  const onCount = entries.filter((entry) => entry.on).length;
  // O lote liga e desliga tarefa por tarefa: nos grupos, as filhas.
  const flat = (list, on) => {
    const out = [];
    list.forEach((entry) => {
      if (entry.task) {
        if (!!entry.task.is_assigned !== on) out.push(entry.task);
        return;
      }
      entry.group.items.forEach((task) => {
        if (!!task.is_assigned !== on && (!on || !task.disabled_at)) out.push(task);
      });
    });
    return out;
  };
  const toTurnOn = flat(filtered, true);
  const toTurnOff = flat(filtered, false);
  const narrowed = !!(term || dept || state !== 'all');
  const scope = narrowed ? 'filtradas' : '';

  if (entries.length === 0) {
    return (
      <Box>
        <Empty>
          <Icon type="check-circle" style={{ color: '#52c41a', marginRight: 6 }} />
          Todas as tarefas do escritório têm regra — não há nada para escolher à mão.
        </Empty>
      </Box>
    );
  }

  return (
    <Box data-manual>
      <Bar>
        <Input
          allowClear
          prefix={<Icon type="search" style={{ color: '#bfbfbf' }} />}
          placeholder="Buscar tarefa sem regra"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          style={{ flex: 1, minWidth: 200 }}
          data-manual-search
        />
        <span style={{ fontSize: 12, color: '#8c8c8c', whiteSpace: 'nowrap' }} data-manual-count>
          <strong style={{ color: '#262626' }}>{onCount}</strong> de {entries.length} ligadas
        </span>
      </Bar>

      <Pills>
        {STATES.map((s) => (
          <Pill key={s.key} type="button" aria-pressed={state === s.key} onClick={() => setState(s.key)} data-manual-state={s.key}>
            {s.label}
            <em>{s.key === 'all' ? entries.length : s.key === 'on' ? onCount : entries.length - onCount}</em>
          </Pill>
        ))}
        {departments.length > 1 && <span style={{ width: 1, background: '#f0f0f0', margin: '0 4px' }} />}
        {departments.length > 1 && departments.map((d) => (
          <Pill key={d.key} type="button" aria-pressed={dept === d.key} onClick={() => setDept(dept === d.key ? null : d.key)} data-manual-dept={d.name}>
            {d.name}
            <em>{d.count}</em>
          </Pill>
        ))}
      </Pills>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '10px 14px 0' }}>
        <Popconfirm
          title={'Ligar ' + say(toTurnOn.length, '1 tarefa', toTurnOn.length + ' tarefas') + (scope ? ' ' + scope : '') + ' para este cliente?'}
          okText="Ligar" cancelText="Cancelar"
          onConfirm={() => onToggle(toTurnOn, true)}
          disabled={toTurnOn.length === 0}
        >
          <Button size="small" icon="check" disabled={toTurnOn.length === 0 || busy.length > 0} data-manual-bulk="on">
            Ligar {narrowed ? 'filtradas' : 'todas'} ({toTurnOn.length})
          </Button>
        </Popconfirm>
        <Popconfirm
          title={'Desligar ' + say(toTurnOff.length, '1 tarefa', toTurnOff.length + ' tarefas') + (scope ? ' ' + scope : '') + ' deste cliente?'}
          okText="Desligar" okType="danger" cancelText="Cancelar"
          onConfirm={() => onToggle(toTurnOff, false)}
          disabled={toTurnOff.length === 0}
        >
          <Button size="small" icon="stop" disabled={toTurnOff.length === 0 || busy.length > 0} data-manual-bulk="off">
            Desligar {narrowed ? 'filtradas' : 'todas'} ({toTurnOff.length})
          </Button>
        </Popconfirm>
      </div>

      <List data-manual-list>
        {filtered.length === 0 ? (
          <Empty>
            <Icon type="search" style={{ marginRight: 6 }} />
            Nenhuma tarefa sem regra com esse filtro.
            {narrowed && (
              <div style={{ marginTop: 6 }}>
                <Button size="small" type="link" onClick={() => { setSearch(''); setState('all'); setDept(null); }}>
                  Limpar filtros
                </Button>
              </div>
            )}
          </Empty>
        ) : sections.map((section) => (
          <div key={section.name}>
            <Group>
              <span>{section.name}</span>
              <span>{section.entries.filter((e) => e.on).length}/{section.entries.length}</span>
            </Group>
            {section.entries.map((entry) => {
              if (entry.group) {
                return (
                  <GroupRow
                    key={entry.key}
                    group={entry.group.group}
                    items={entry.group.items}
                    status={entry.group.status}
                    onToggle={onToggle}
                    busyIds={busy}
                    defaultOpen={!!term && !has(entry.name)}
                  />
                );
              }
              const task = entry.task;
              const on = !!task.is_assigned;
              const loading = busy.indexOf(task.id) > -1;
              return (
                <Item
                  key={entry.key}
                  role="switch"
                  aria-checked={on}
                  data-manual-task={task.name}
                  onClick={() => { if (!busy.length) onToggle([task], !on); }}
                >
                  <Switch size="small" checked={on} loading={loading} />
                  <span className="name">
                    {task.name}
                    {task.disabled_at && <Tag color="red" style={{ marginLeft: 8 }}>desativada</Tag>}
                    {/* Por que está aqui: decidida como manual, ou regra ainda por definir */}
                    {isMarkedNoRule(task) ? (
                      <Tooltip title={NO_RULE_HELP} overlayStyle={{ maxWidth: 320 }}>
                        <Tag color="purple" style={{ marginLeft: 8 }} data-manual-why="none">não possui regra</Tag>
                      </Tooltip>
                    ) : (
                      <Tooltip title={MISSING_RULE_HELP} overlayStyle={{ maxWidth: 320 }}>
                        <Tag color="orange" style={{ marginLeft: 8 }} data-manual-why="missing">regra a definir</Tag>
                      </Tooltip>
                    )}
                  </span>
                  <span className="when">{task.first_date ? frequency(task.frequency, task) : ''}</span>
                </Item>
              );
            })}
          </div>
        ))}
      </List>
    </Box>
  );
};

export default ManualTasks;
